import type { CarConfigDispatchType } from "../redux/store";
import { useCarConfigSelector } from "../redux/store";
import type {
    ColourType,
    ExtraType,
    PackAvailabilityType,
    PackType,
    PriceType,
    UpholsteryType,
    WheelType,
} from "../../../../common-deprecated/types/CommonTypes";
import { find } from "../../../../common-deprecated/utils";
import { getPackItems } from "../../../shared-logic/utils/packUtils";
import { addPack, addPackExtra, removePack, removePackExtra } from "../redux/slices/CarConfigBasketSlice";
import Debug from "../../../../common-deprecated/Debug";
import { addNotification } from "../../../../common-deprecated/redux/actions/NotificationActions";
import useCarConfigNotifications from "./useCarConfigNotifications";
import { useDispatch } from "../../../../shared-logic/redux/store";

type useAphPackType = {
    actions: {
        selectPack: () => void;
        deselectPack: () => void;
        selectPackExtra: (extra: ExtraType) => void;
        deselectPackExtra: (extra: ExtraType) => void;
        displayNotifications: () => void;
    };
    packContent: {
        selected: boolean;
        availability: PackAvailabilityType;
        price: PriceType;
        selectedEquipment: string[];
        optionalEquipment: ExtraType[];
        colours: ColourType[];
        upholsteries: (UpholsteryType | ExtraType)[];
        wheels: WheelType[];
        renderedEquipment: (UpholsteryType | ExtraType | WheelType)[];
        totalPackContent: number;
    };
};

const useAphPack = (pack: PackType): useAphPackType | null => {
    const { query } = useCarConfigSelector((state) => state.commonSettings);
    const useLbxJourney = query.useLbxJourney || false;
    const basket = useCarConfigSelector((state) => state.basket);
    const items = useCarConfigSelector((state) => state.items);
    const financeOption = useCarConfigSelector((state) => state.utils.financeOption);
    const dispatch = useDispatch<CarConfigDispatchType>();
    const { getNotificationsToDisplay } = useCarConfigNotifications();

    // Check if pack is present in basket
    const isSelected = !!find(pack.id, basket.packs);
    const selectedExtras = basket.packExtras.map((extra) => extra.id);
    const packSelected = pack.standard || isSelected;

    const packContent = getPackItems(pack, basket, items.upholsteries, financeOption);
    // This is done here and not in the formatters as otherwise it might interfere with the include/exclude rules
    if (
        packContent.renderedEquipment.length <= 0 &&
        packContent.optionalEquipment.length <= 0 &&
        packContent.wheels.length <= 0 &&
        packContent.upholsteries.length <= 0 &&
        packContent.colours.length <= 0
    ) {
        Debug.warn(`Pack ${pack.name} doesn't have any items and is not shown`, {
            path: query.path,
            packId: pack.id,
            status: "warning",
        });
        return null;
    }

    const actions = {
        selectPack: (): void => {
            dispatch(addPack({ pack, selectedPacks: [], selectedExtras: [], automatic: false }));
        },
        deselectPack: (): void => {
            dispatch(removePack({ pack }));
        },
        // IDE says it is not use but it is used (for the 3 functions below)
        selectPackExtra: (extra: ExtraType): void => {
            dispatch(addPackExtra({ pack, extra, selectedExtras: [], automatic: false }));
        },
        deselectPackExtra: (extra: ExtraType): void => {
            dispatch(removePackExtra({ extra }));
        },
        displayNotifications: (): void => {
            const notifications = getNotificationsToDisplay({
                colours: !!packContent.colours.length,
                upholsteries: !!packContent.upholsteries.length,
                useLbxJourney,
            });
            if (notifications.length) dispatch(addNotification(notifications));
        },
    };

    return {
        actions,
        packContent: {
            ...packContent,
            selected: packSelected,
            selectedEquipment: isSelected ? selectedExtras : [],
        },
    };
};

export default useAphPack;
